import React from 'react';
import type { WidgetConfig } from './types';

interface WidgetButtonProps {
    position: WidgetConfig['position'];
    unreadCount: number;
    onClick: () => void;
    hasNewNotification: boolean;
}

/**
 * Floating button component that displays the Atlas bot icon with notification badge
 */
export const WidgetButton: React.FC<WidgetButtonProps> = ({
    position,
    unreadCount,
    onClick,
    hasNewNotification,
}) => {
    const positionStyles = getPositionStyles(position);

    return (
        <button
            className={`atlas-widget-button ${hasNewNotification ? 'atlas-widget-button--pulse' : ''}`}
            style={positionStyles}
            onClick={onClick}
            aria-label={`Open FleetGO AI Assistant${unreadCount > 0 ? `, ${unreadCount} unread notifications` : ''}`}
            title="Open FleetGO AI Assistant"
        >
            <img
                src={getBotImageUrl()}
                alt="FleetGO AI Assistant"
                className="atlas-widget-button__icon"
                width={40}
                height={40}
            />

            {/* Notification Badge */}
            {unreadCount > 0 && (
                <span className="atlas-widget-button__badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                </span>
            )}
        </button>
    );
};

/**
 * Get the bot image URL - uses the public bot.png from the widget host
 */
function getBotImageUrl(): string {
    // Try to get the script source to determine the base URL
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src;
        if (src && src.includes('atlas-widget')) {
            const baseUrl = src.substring(0, src.lastIndexOf('/'));
            return `${baseUrl}/bot.png`;
        }
    }
    // Fallback to relative path
    return '/bot.png';
}

/**
 * Get CSS position styles based on the configured position
 * If position is null, returns inline styles (no fixed positioning)
 */
function getPositionStyles(position: WidgetConfig['position']): React.CSSProperties {
    // Inline mode: render as a normal inline element
    if (position === null) {
        return {
            position: 'relative',
            pointerEvents: 'auto',
        };
    }

    // Fixed position mode: render as a floating button
    const base: React.CSSProperties = {
        position: 'fixed',
        zIndex: 999998,
        pointerEvents: 'auto',
    };

    switch (position) {
        case 'bottom-right':
            return { ...base, bottom: '24px', right: '24px' };
        case 'bottom-left':
            return { ...base, bottom: '24px', left: '24px' };
        case 'top-right':
            return { ...base, top: '24px', right: '24px' };
        case 'top-left':
            return { ...base, top: '24px', left: '24px' };
        default:
            return { ...base, bottom: '24px', right: '24px' };
    }
}
